import { FormEvent, useState, type Dispatch, type SetStateAction } from 'react';
import {
  activateUser,
  createUser,
  deactivateUser,
  updateMemberJoinedOn,
  type ClubMemberRole,
} from '../../../api';
import { User } from '../../../types';
import { canHaveOwnershipShare } from '../../../utils/isGeneralUser';
import { getApiErrorMessage } from '../../../utils/apiError';
import {
  emptyAddForm,
  isPlausibleIsoDate,
  roleLabel,
  todayIso,
} from '../membersUtils';

export type AddTab = 'invite' | 'manual';

type OpenSharesModal = (
  list?: User[],
  activatingIds?: number[],
  leaveUserId?: number | null,
  restoreUserId?: number | null,
  restoreAs?: 'pilot' | 'admin' | null
) => void;

export function useMemberLifecycle({
  members,
  loadMembers,
  openSharesModal,
  setInviteLink,
  setJoinedDrafts,
  showError,
  showSuccess,
}: {
  members: User[];
  loadMembers: () => Promise<User[]>;
  openSharesModal: OpenSharesModal;
  setInviteLink: (link: string | null) => void;
  setJoinedDrafts: Dispatch<SetStateAction<Record<number, string>>>;
  showError: (message: string) => void;
  showSuccess: (message: string) => void;
}) {
  const [addOpen, setAddOpen] = useState(false);
  const [addTab, setAddTab] = useState<AddTab>('invite');
  const [form, setForm] = useState(emptyAddForm);
  const [deactivating, setDeactivating] = useState<User | null>(null);
  const [restoring, setRestoring] = useState<User | null>(null);
  const [restoreRole, setRestoreRole] = useState<ClubMemberRole>('technician');
  const [busyId, setBusyId] = useState<number | null>(null);
  const [adding, setAdding] = useState(false);

  const closeAddModal = () => {
    setAddOpen(false);
    setInviteLink(null);
    setForm(emptyAddForm());
    setAddTab('invite');
  };

  const handleAdd = async (e: FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.login.trim() || !form.password) {
      showError('Заполните имя, логин и пароль');
      return;
    }
    if (form.password.length < 4) {
      showError('Пароль должен быть не короче 4 символов');
      return;
    }
    const selectedRole = form.role;
    setAdding(true);
    try {
      const created = await createUser({
        name: form.name.trim(),
        login: form.login.trim(),
        password: form.password,
        role: selectedRole,
        joined_on: form.joined_on || todayIso(),
      });
      closeAddModal();
      const list = await loadMembers();
      if (created.data.role === 'pending') {
        showSuccess(`«${created.data.name}» в ожидании — назначьте доли`);
        openSharesModal(list, [created.data.id]);
      } else {
        showSuccess(`Участник добавлен (${roleLabel(selectedRole)})`);
      }
    } catch (error) {
      showError(getApiErrorMessage(error, 'Ошибка добавления участника'));
    }
    setAdding(false);
  };

  const handleDeactivateConfirm = async () => {
    if (!deactivating) return;
    if (canHaveOwnershipShare(deactivating.role, deactivating.is_pilot)) {
      const leaving = deactivating;
      setDeactivating(null);
      openSharesModal(members, [], leaving.id);
      return;
    }
    setBusyId(deactivating.id);
    try {
      await deactivateUser(deactivating.id);
      setDeactivating(null);
      showSuccess(
        deactivating.role === 'guest'
          ? 'Гость удалён'
          : 'Участник деактивирован'
      );
      await loadMembers();
    } catch (error) {
      showError(getApiErrorMessage(error, 'Ошибка удаления'));
    }
    setBusyId(null);
  };

  const handleActivate = (member: User) => {
    setRestoreRole('technician');
    setRestoring(member);
  };

  const handleRestoreConfirm = async () => {
    if (!restoring) return;
    const member = restoring;
    const role = restoreRole;
    setRestoring(null);

    if (canHaveOwnershipShare(role)) {
      const asOwner: 'pilot' | 'admin' =
        role === 'admin' ? 'admin' : 'pilot';
      openSharesModal(members, [], null, member.id, asOwner);
      return;
    }

    setBusyId(member.id);
    try {
      await activateUser(member.id, role);
      showSuccess(`Участник восстановлен как ${roleLabel(role)}`);
      await loadMembers();
    } catch (error) {
      showError(getApiErrorMessage(error, 'Ошибка восстановления'));
    }
    setBusyId(null);
  };

  const commitJoinedOn = async (target: User, value: string) => {
    if (!value || value === target.joined_on || !isPlausibleIsoDate(value)) {
      setJoinedDrafts((prev) => {
        if (prev[target.id] === undefined) return prev;
        const next = { ...prev };
        delete next[target.id];
        return next;
      });
      return;
    }

    // Держим новую дату в поле, пока идёт сохранение (не мигаем старой).
    setJoinedDrafts((prev) => ({ ...prev, [target.id]: value }));
    setBusyId(target.id);
    try {
      await updateMemberJoinedOn(target.id, value);
      showSuccess(`Дата «Собственник с» для «${target.name}» обновлена`);
      await loadMembers();
    } catch (error) {
      showError(getApiErrorMessage(error, 'Ошибка даты включения'));
    }
    setJoinedDrafts((prev) => {
      if (prev[target.id] === undefined) return prev;
      const next = { ...prev };
      delete next[target.id];
      return next;
    });
    setBusyId(null);
  };

  return {
    addOpen,
    setAddOpen,
    addTab,
    setAddTab,
    form,
    setForm,
    deactivating,
    setDeactivating,
    restoring,
    setRestoring,
    restoreRole,
    setRestoreRole,
    busyId,
    setBusyId,
    adding,
    closeAddModal,
    handleAdd,
    handleDeactivateConfirm,
    handleActivate,
    handleRestoreConfirm,
    commitJoinedOn,
  };
}
